import { useEffect, useRef, useState } from 'react';
import Modal from '../components/Modal.tsx';
import { X } from '../lib/icons.tsx';

type DescribeSlideModalProps = {
  /** Prose stating where the slide will land — composed by the caller, which
   *  is the only thing that knows the section and the reference slide. */
  where: string;
  onCancel: () => void;
  onSubmit: (description: string) => void;
  busy?: boolean;
};

/**
 * "Describe it in chat", as a room you finish in.
 *
 * The button used to close the add-slide gallery, force deck scope, open the
 * Conversation panel and drop a half-written sentence in the composer. Nothing
 * was broken about that, but it read as being thrown OUT of the task: you
 * pressed a button in a picking room and landed in the whole-deck chat with
 * homework. This asks the one question it actually needs and then starts the
 * turn itself, the way the New Section modal does.
 *
 * It deliberately collects ONE thing. The position was already settled by the
 * + you pressed, so it is stated rather than re-asked — offering a control for
 * it would be a second answer to a question the user has already given.
 *
 * It owns no chat lifecycle: the parent hands the composed ask to the existing
 * autoSend path, so there is exactly one run loop in the editor.
 */
export default function DescribeSlideModal({ where, onCancel, onSubmit, busy }: DescribeSlideModalProps) {
  const [text, setText] = useState('');
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    areaRef.current?.focus();
  }, []);

  const ready = text.trim().length > 0 && !busy;
  function submit() {
    if (!ready) return;
    onSubmit(text.trim());
  }

  return (
    <Modal
      ariaLabel="Describe the slide"
      onClose={onCancel}
      className="modal--describe-slide"
      backdropClassName="modal-backdrop--describe"
      // Opens ON TOP of the add-slide gallery, which must not close under it.
      nested
    >
      <header className="modal__header new-deck__head">
        <h2 className="modal__title" id="describe-slide-title">
          Describe the slide
        </h2>
        <span className="add-slide__spacer" />
        <button type="button" className="iconbtn" onClick={onCancel} aria-label="Close" title="Close">
          <X />
        </button>
      </header>

      <div className="modal__body new-deck__body">
        {/* Stated, not asked: the insert point already answered this. */}
        <p className="muted small describe-slide__where">{where}</p>
        <textarea
          ref={areaRef}
          className="new-deck__notes describe-slide__input"
          rows={5}
          value={text}
          placeholder="e.g. A three-column comparison of our pricing tiers, with the middle one highlighted"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // The composer's own shortcut, so the habit carries over.
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
      </div>

      <footer className="modal__footer new-deck__foot">
        <span className="muted small new-deck__foot-hint">
          The AI writes the slide and shows it for review before anything changes.
        </span>
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" onClick={submit} disabled={!ready}>
          {busy ? 'Starting…' : 'Create slide'}
        </button>
      </footer>
    </Modal>
  );
}
